import { IconArchive, IconRefresh, IconPlus, IconBox, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import MessageModal from "../../../components/MessageModal";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useUpdateShelveStatusMutation } from "../../../apis/shelveApis";
import CreatePalletModal from "./CreatePalletModal";

interface FloorElementProps {
  floorId: string;
  floorName: string;
  floor?: any;
  slots: Array<{ id: string; slotName: string }>;
  fetchShelves: () => void;
  warehouseId?: string;
  clientId?: string;
  areaId?: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
  isSelected?: boolean;
  onPalletClick?: (pallet: any) => void;
  isParentRowArchived?: boolean;
}

const FloorElement: React.FC<FloorElementProps> = ({
  floorId,
  floorName,
  floor,
  slots,
  fetchShelves,
  warehouseId,
  clientId,
  areaId,
  onClick,
  onDoubleClick,
  isSelected,
  onPalletClick,
  isParentRowArchived,
}) => {
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [palletModalOpen, setPalletModalOpen] = useState(false);
  const [palletConfirmModal, setPalletConfirmModal] = useState<{
    open: boolean;
    pallet: any;
    action: 'archive' | 'restore';
  }>({ open: false, pallet: null, action: 'archive' });
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    setStartIndex(0);
  }, [floorId, slots?.length]);
  const isActive = floor?.status !== "INACTIVE";
  const isDisabled = isParentRowArchived || !isActive;

  const { mutate: toggleStatus, isPending } = useUpdateShelveStatusMutation();
  const { mutate: updatePalletStatus, isPending: isUpdatePalletPending } = useUpdateShelveStatusMutation();

  const handleToggle = () => {
    // pallet === "INACTIVE" ? "ACTIVE" : "INACTIVE";

    toggleStatus(
      {
        id: floorId,
        status: isActive ? "INACTIVE" : "ACTIVE",
        model: "floor", // ✅ this is key
      },
      {
        onSuccess: () => {
          toast.success(
            isActive ? "Floor area disabled" : "Floor area enabled",
          );

          setStatusModalOpen(false);
          fetchShelves();
        },
        onError: (error: any) => {
          toast.error(error.message || "Failed to update floor status");
        },
      },
    );
  };
  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick?.();
        }}
        className={`w-full transition-all cursor-pointer ${!isActive ? "opacity-50 grayscale" : ""}`}
      >
        <div
          className={`border py-3 px-1.5 rounded-xl space-y-3 group transition-all ${
            isSelected
              ? "border-[#FBD037] bg-yellow-50 shadow-md"
              : isActive
              ? "bg-white border-gray-200 hover:shadow-md"
              : "bg-gray-50 border-gray-300"
          }`}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-bold text-gray-800">{floorName}</h4>
              {isActive && (
                <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-100 text-purple-700">
                  Floor Area
                </span>
              )}
            </div>
            {!isActive && (
              <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-200 text-gray-600">
                Inactive
              </span>
            )}

            {/* <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
              {isActive && (
                <button 
                  onClick={() => setPalletModalOpen(true)}
                  className="p-1 hover:bg-gray-100 rounded text-gray-400"
                >
                  <IconPlus size={18} />
                </button>
              )}
              <button
                onClick={() => setStatusModalOpen(true)}
                className={`p-1 rounded ${isActive ? "text-red-400 hover:bg-red-50" : "text-blue-500 hover:bg-blue-50"}`}
              >
                {isActive ? (
                  <IconArchive size={18} />
                ) : (
                  <IconRefresh size={18} />
                )}
              </button>
            </div> */}
          </div>
          
          {/* Display Pallets */}
          {slots && slots.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 w-full">
                {/* Left Arrow */}
                {startIndex > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setStartIndex((prev) => Math.max(0, prev - 2));
                    }}
                    className="flex items-center justify-center w-5 h-10 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition shrink-0"
                    title="Previous"
                  >
                    <IconChevronLeft size={12} stroke={2.5} />
                  </button>
                )}

                {/* Grid for Pallets */}
                <div className="grid grid-cols-2 gap-2 flex-1 items-stretch">
                  {slots.slice(startIndex, startIndex + 2).map((slot: any) => {
                    const isSlotActiveState = slot.status !== "INACTIVE";
                    const isArchived = isDisabled || !isSlotActiveState;
                    const isOccupied = isSlotActiveState && (slot.currentInventoryCount || 0) > 0;
                    const slotName = slot.slotName || slot.name;

                    const bgClass = isArchived
                      ? "bg-zinc-100/70 border-zinc-200 text-zinc-400 opacity-60 cursor-not-allowed"
                      : isOccupied
                        ? "bg-green-50 border-green-300 text-green-800 cursor-pointer hover:bg-green-100/60 hover:shadow-md hover:scale-[1.02]"
                        : "bg-amber-50 border-amber-300 text-amber-900 cursor-pointer hover:bg-amber-100/60 hover:shadow-md hover:scale-[1.02]";

                    const textClass = isArchived
                      ? "text-zinc-400"
                      : isOccupied
                        ? "text-green-800"
                        : "text-amber-900";

                    const subtextClass = isArchived
                      ? "text-zinc-400"
                      : isOccupied
                        ? "text-green-600/90 font-medium"
                        : "text-amber-600/90 font-medium";

                    const iconClass = isArchived
                      ? "text-zinc-400"
                      : isOccupied
                        ? "text-green-700/80"
                        : "text-amber-600";

                    const progressBgClass = isArchived
                      ? "bg-zinc-300"
                      : isOccupied
                        ? "bg-green-600"
                        : "bg-amber-400";

                    const progressWidth = isOccupied ? "90%" : "0%";

                    return (
                      <div key={slot.id} className="relative group/pallet w-full">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) {
                              onPalletClick?.(slot);
                            }
                          }}
                          className={`p-2 border rounded-xl transition-all ${bgClass}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="text-left truncate flex-1 min-w-0">
                              <div className={`text-xs font-bold leading-tight truncate ${textClass}`} title={slotName}>
                                {slotName}
                              </div>
                              <div className={`text-[9px] mt-0.5 ${subtextClass} truncate`}>
                                {isArchived ? "Archived" : "Pallet"}
                              </div>
                            </div>
                            <IconBox className={`${iconClass} mt-0.5 shrink-0 ml-1`} size={14} stroke={1.5} />
                          </div>
                          <div className="w-full h-1 bg-slate-200/70 rounded-full overflow-hidden mt-1.5">
                            <div 
                              className={`h-full rounded-full ${progressBgClass}`} 
                              style={{ width: progressWidth }} 
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right Arrow */}
                {startIndex + 2 < slots.length && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setStartIndex((prev) => prev + 2);
                    }}
                    className="flex items-center justify-center w-5 h-10 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition shrink-0"
                    title="Next"
                  >
                    <IconChevronRight size={12} stroke={2.5} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <MessageModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title={isActive ? "Archive Floor Area?" : "Reactivate Floor Area?"}
        subtitle={
          isActive
            ? "Hide this area from pickers."
            : "Return this area to active inventory use."
        }
        img={isActive ? "/img/archive.png" : "/img/restore.png"}
        showFooter={true}
        actionButtonText={isActive ? "Confirm Archive" : "Confirm Restore"}
        actionButtonOnClick={handleToggle}
        isPending={isPending}
      />

      {palletModalOpen && (
        <CreatePalletModal
          open={palletModalOpen}
          onClose={() => setPalletModalOpen(false)}
          floorId={floorId}
          floorName={floorName}
          warehouseId={warehouseId || ""}
          clientId={clientId || ""}
          areaId={areaId || ""}
          fetchShelves={fetchShelves}
        />
      )}
      <MessageModal
        open={palletConfirmModal.open}
        onClose={() => setPalletConfirmModal({ open: false, pallet: null, action: 'archive' })}
        title={palletConfirmModal.action === 'archive' ? 'Archive Pallet?' : 'Restore Pallet?'}
        subtitle={
          palletConfirmModal.action === 'archive'
            ? `This will hide pallet ${palletConfirmModal.pallet?.slotName || palletConfirmModal.pallet?.name || 'Unknown'} from daily operations.`
            : `This will make pallet ${palletConfirmModal.pallet?.slotName || palletConfirmModal.pallet?.name || 'Unknown'} available again.`
        }
        img={palletConfirmModal.action === 'archive' ? '/img/archive.png' : '/img/reset.png'}
        showFooter={true}
        actionButtonText={palletConfirmModal.action === 'archive' ? 'Archive Pallet' : 'Restore Pallet'}
        actionButtonOnClick={() => {
          updatePalletStatus(
            {
              id: palletConfirmModal.pallet.id,
              status: palletConfirmModal.action === 'archive' ? "INACTIVE" : "ACTIVE",
              model: "pallet"
            },
            {
              onSuccess: () => {
                toast.success(
                  palletConfirmModal.action === 'archive'
                    ? `Pallet ${palletConfirmModal.pallet?.slotName || palletConfirmModal.pallet?.name || 'Unknown'} archived`
                    : `Pallet ${palletConfirmModal.pallet?.slotName || palletConfirmModal.pallet?.name || 'Unknown'} restored`
                );
                setPalletConfirmModal({ open: false, pallet: null, action: 'archive' });
                fetchShelves();
              },
              onError: (error: any) => {
                toast.error(`Failed to ${palletConfirmModal.action} pallet: ${error.message}`);
              }
            }
          );
        }}
        isPending={false}
      />
    </>
  );
};

export default FloorElement;
